/**
 * Editing an account — RF-0003, and the rename half of FR-0004 with it.
 *
 * `AccountDetail` contained **no mutation at all**: the page was read-only end
 * to end while `PUT /api/accounts/:id` had always accepted a partial of
 * `createAccountSchema`. So this is a form over an endpoint that was already
 * there. No new route, no new permission.
 *
 * Three things this form is careful about:
 *
 *  - **`website` and `hq_country` are normalised before the write**, through
 *    `lib/account-fields`, which the create dialog uses too. Both carry a
 *    check constraint (`^https://`, `^[A-Z]{2}$`) and a form that posts
 *    `example.com` or `us` earns a raw `23514` naming the constraint.
 *  - **Changing `channel_type` here changes the account and nothing else.**
 *    An opportunity copies `channel_type` and `price_basis` once, at creation
 *    (`NewOpportunity.tsx`). Rewriting existing opportunities would silently
 *    restate revenue on the distributor basis, so this sends the account's own
 *    column and nothing cascades.
 *  - **It sends only what changed.** A `PUT` carrying every field would
 *    stamp `updated_by` over columns nobody touched and make §6.10's
 *    concurrency notice meaningless.
 *
 * `export_sensitive` is **not** on this form: §8 item 6 says a human sets it
 * on the compliance surface, never a general-purpose edit box, and
 * `CreateAccountDialog` omits it for the same reason.
 */

import { useState } from 'react';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Field } from '@/components/ui/label';
import { useApiMutation, type Row } from '@/lib/queries';
import { useReferenceData } from '@/lib/reference';
import { SEGMENTS, REGIONS, CHANNEL_TYPES, ACCOUNT_STATUSES } from '@menlo/shared';
import { humanize } from '@/lib/format';
import { normaliseCountry, normaliseWebsite, optionalText } from '@/lib/account-fields';
import { ParentAccountField } from '@/features/pickers/AccountPicker';

const TEXT_FIELDS = [
  ['address_line1', 'Address line 1'],
  ['address_line2', 'Address line 2'],
  ['city', 'City'],
  ['state_region', 'State / region'],
  ['postal_code', 'Postal code'],
] as const;

const text = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value);

export function EditAccountDialog({
  account,
  onClose,
  onSaved,
}: {
  account: Row;
  onClose: () => void;
  onSaved: () => void;
}): React.ReactElement {
  const reference = useReferenceData();

  const [name, setName] = useState(text(account['name']));
  const [segment, setSegment] = useState(text(account['segment']));
  const [region, setRegion] = useState(text(account['region']));
  const [channelType, setChannelType] = useState(text(account['channel_type']));
  const [repFirmId, setRepFirmId] = useState(text(account['rep_firm_id']));
  const [status, setStatus] = useState(text(account['status']));
  const [website, setWebsite] = useState(text(account['website']));
  const [hqCountry, setHqCountry] = useState(text(account['hq_country']));
  const [parentId, setParentId] = useState<string | null>(
    typeof account['parent_account_id'] === 'string' ? account['parent_account_id'] : null,
  );
  /** The parent as the account page resolved it: live, with its name. */
  const currentParent =
    typeof account['parent_account_id'] === 'string' &&
    typeof account['parent_account_name'] === 'string'
      ? { id: account['parent_account_id'], name: account['parent_account_name'] }
      : null;
  const [address, setAddress] = useState<Record<string, string>>(() =>
    Object.fromEntries(TEXT_FIELDS.map(([column]) => [column, text(account[column])])),
  );

  const websiteValue = normaliseWebsite(website);
  const countryValue = normaliseCountry(hqCountry);
  const invalid = name.trim() === '' || websiteValue.error !== null || countryValue.error !== null;

  const changes = (): Record<string, unknown> => {
    const body: Record<string, unknown> = {};
    const put = (column: string, next: unknown): void => {
      const before = account[column] ?? null;
      if (next !== before) body[column] = next;
    };

    put('name', name.trim());
    put('segment', segment);
    put('region', region);
    put('channel_type', channelType);
    put('status', status);
    put('rep_firm_id', repFirmId === '' ? null : repFirmId);
    put('parent_account_id', parentId);
    put('website', websiteValue.value);
    put('hq_country', countryValue.value);
    for (const [column] of TEXT_FIELDS) put(column, optionalText(address[column] ?? ''));
    return body;
  };

  const save = useApiMutation<Record<string, unknown>, void>(
    () => ({ path: `/api/accounts/${String(account['id'])}`, method: 'PUT', body: changes() }),
    { invalidate: [['accounts'], ['account-summary', String(account['id'])]], onSuccess: onSaved },
  );

  const nothingChanged = Object.keys(changes()).length === 0;

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title="Edit account"
      description="Everything here is the account's own record. Opportunities already created keep the channel and price basis they were created with."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => save.mutate()}
            disabled={invalid || nothingChanged || save.isPending}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name">
          <Input value={name} onChange={(event) => setName(event.target.value)} autoFocus />
        </Field>

        <ParentAccountField
          value={parentId}
          onChange={setParentId}
          current={currentParent}
          exclude={[String(account['id'])]}
        />

        <div className="grid grid-cols-2 gap-3">
          <Field label="Segment">
            <Select value={segment} onChange={(event) => setSegment(event.target.value)}>
              {SEGMENTS.map((value) => (
                <option key={value} value={value}>
                  {humanize(value)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Region">
            <Select value={region} onChange={(event) => setRegion(event.target.value)}>
              {REGIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label="Channel"
            hint="Changes this account only. Existing opportunities keep the basis they were created with."
          >
            <Select value={channelType} onChange={(event) => setChannelType(event.target.value)}>
              {CHANNEL_TYPES.map((value) => (
                <option key={value} value={value}>
                  {humanize(value)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Status">
            <Select value={status} onChange={(event) => setStatus(event.target.value)}>
              {ACCOUNT_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {humanize(value)}
                </option>
              ))}
            </Select>
          </Field>
          {channelType !== 'direct' && (
            <Field label="Rep firm" hint="Sets the net factor that converts resale to net.">
              <Select value={repFirmId} onChange={(event) => setRepFirmId(event.target.value)}>
                <option value="">—</option>
                {reference.rep_firms.map((firm) => (
                  <option key={firm.id} value={firm.id}>
                    {firm.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          {/* The error sits outside `Field`'s implicit label, or a screen
              reader announces it as part of the control's name. */}
          <div className="space-y-1">
            <Field label="Website" hint="https:// is added if you leave it off.">
              <Input
                value={website}
                placeholder="example.com"
                onChange={(event) => setWebsite(event.target.value)}
              />
            </Field>
            {websiteValue.error !== null && (
              <p className="text-[11px] font-medium text-red-700">{websiteValue.error}</p>
            )}
          </div>
          <div className="space-y-1">
            <Field label="HQ country" hint="Two-letter ISO code; case does not matter.">
              <Input
                value={hqCountry}
                placeholder="US"
                onChange={(event) => setHqCountry(event.target.value)}
              />
            </Field>
            {countryValue.error !== null && (
              <p className="text-[11px] font-medium text-red-700">{countryValue.error}</p>
            )}
          </div>
        </div>

        <fieldset className="space-y-3">
          <legend className="text-xs font-medium text-muted-foreground">Address</legend>
          <div className="grid grid-cols-2 gap-3">
            {TEXT_FIELDS.map(([column, label]) => (
              <Field key={column} label={label}>
                <Input
                  value={address[column] ?? ''}
                  onChange={(event) =>
                    setAddress((all) => ({ ...all, [column]: event.target.value }))
                  }
                />
              </Field>
            ))}
          </div>
        </fieldset>
      </div>
    </Dialog>
  );
}
